import {
	Component,
	OnInit,
	ElementRef,
	ViewChild,
	AfterViewInit,
	Output,
	EventEmitter,
	Input,
	ChangeDetectorRef,
} from "@angular/core";
import { GlobalUtilsService } from "src/app/services/ui/global-utils.service";
import { environment } from "src/environments/environment";
declare const WebViewer: any;

export interface PdfTronCustomButton {
	type: string;
	img?: string;
	onClick?: () => void;
	dataElement?: string;
	title?: string;
	hidden?: string[];
}

export interface DocViewerDocumentDetails {
	documentId?: number;
	jobId?: number;
	jobDocumentId?: number;
	content: Blob;
	documentDisplayName: string;
	documentDownloadName: string;
	fileName?: string;
	isFitToPage?: boolean;
}

@Component({
	selector: "app-document-viewer",
	templateUrl: "./document-viewer.component.html",
	styleUrls: ["./document-viewer.component.scss"],
})
export class DocumentViewerComponent implements OnInit, AfterViewInit {
	@ViewChild("viewer") public viewer: ElementRef;

	// tslint:disable-next-line:no-output-native
	@Output() public close: EventEmitter<boolean> = new EventEmitter();
	@Output() public documentNotSupported: EventEmitter<DocViewerDocumentDetails> =
		new EventEmitter();

	@Input() public isLoading = false;

	@Input() public afterWebViewerRender: (instance) => void;

	@Input() public isCloseDisabled: boolean = false;
	@Input() public isCloseEnabled: boolean = false;

	@Input()
	public set documentDetails(value: DocViewerDocumentDetails) {
		
		this.fileName = null;
		this.isLoadingFailed = false;
		this.isLoading = false;
		this.loaderMessage = null;
		this.isDocumentLoading = false;
		this.isFitToPage = value?.isFitToPage ?? false;
		if (!!value && !!value.content) {
			this._documentDetails = value;
			if (this.wvInstance) {
				this.loadDocument(
					value.content,
					value.documentDisplayName,
					this.docName(value)
				);
			}
			this.changeDetector.detectChanges();
		} else {
			this._documentDetails = null;
			this.isLoadingFailed = true;
			if (!value) {
				this.loaderMessage = "Loading document...";
			} else {
				this.loaderMessage = "The file seems to be missing or cannot be loaded.";
			}
			this.changeDetector.detectChanges();
		}
	}

	public get documentDetails() {
		return this._documentDetails;
	}

	public docName(doc: DocViewerDocumentDetails): string {
		return !doc ? null : doc.fileName ?? doc.documentDisplayName;
	}

	public fileName: string = null;
	public isDocumentLoading = false;
	public isLoadingFailed = false;
	public loaderMessage = null;
	public isViewerReady = false;
	public isFitToPage = false;
	public isMobileDevice = false;

	public validDocumentExtensions: string[] = [
		"pdf",
		"xfdf",
		"fdf",
		"doc",
		"docx",
		"xls",
		"xlsx",
		"ppt",
		"pptx",
		"jpg",
		"jpeg",		
		"png",
		"jfif",
	];

	// tslint:disable-next-line:variable-name
	private _documentDetails: DocViewerDocumentDetails = null;
	private wvInstance: any;
	public instance: any;

	constructor(private changeDetector: ChangeDetectorRef) { }

	public ngOnInit(): void {
		this.wvDocumentLoadedHandler = this.wvDocumentLoadedHandler.bind(this);
		this.isMobileDevice = GlobalUtilsService.isMobileDevice;
	}

	public ngAfterViewInit(): void {
		this.setWebViewer();
	}

	public viewDocument(content: Blob, fileName: string): void {
		this.loadDocument(content, null, fileName);
	}

	public closeSlide(): void {
		this.wvInstance.UI.closeDocument();
		this._documentDetails = null;
		this.close.emit(true);
	}

	private loadDocument(
		content: Blob,
		displayFileName: string,
		documentFileName: string
	): void {
		this.isDocumentLoading = true;
		if(!documentFileName){
			this.invalidDocument();
			return;
		}
		const fileNameParts = documentFileName.split(".");

		if (
			!this.validDocumentExtensions.includes(
				fileNameParts[fileNameParts.length - 1]?.toLowerCase()
			)
		) {
			this.invalidDocument();
			return;
		}
		if (!displayFileName) {
			// fileNames can contain multiple dots, but extension is the last dot-separated segment.
			// Therefore, fileName is the join of all previous segments (1 or more)
			this.fileName = documentFileName
				.split(".")
				.filter((_, index, arr) => index === 0 || index < arr.length - 1)
				.join(".");
		} else {
			this.fileName = displayFileName;
		}

		const file = new File([content], documentFileName);
		this.wvInstance.UI.loadDocument(file, { filename: documentFileName });
	}

	private wvDocumentLoadedHandler(): void {
		const annotManager = this.wvInstance.Core.annotationManager;
		const { Annotations } = this.wvInstance.Core;
		const rectangle = new Annotations.RectangleAnnotation();
		rectangle.PageNumber = 1;
		rectangle.X = 100;
		rectangle.Y = 100;
		rectangle.Width = 250;
		rectangle.Height = 250;
		rectangle.StrokeThickness = 5;
		rectangle.Author = annotManager.getCurrentUser();

		this.isDocumentLoading = false;
		this.changeDetector.detectChanges();
	}

	private setWebViewer(content: Blob = null, fileName: string = null): void {
		const disabledElements = [
			"contextMenuPopup",
			"viewControlsButton",
			"viewControlsOverlay",
			"menuButton",
			"toggleNotesButton",
			"ribbons",
			"toolsOverlay",
			"selectToolButton",
			"toolsHeader",
			"pageInsertionHeader",
			"insertPageAbove",
			"insertPageBelow",
			"pageManipulationHeader",
			"replacePage",
			"extractPage",
			"deletePage",
			"thumbDelete",
			"pageManipulationOverlayButton",
			// "textSelectButton"			
		];
		if(GlobalUtilsService.isMobileDevice) {
			disabledElements.push("searchButton");
			disabledElements.push("moreButton");
			disabledElements.push("panToolButton");
			disabledElements.push("leftPanelButton")
		}
		//NOTE: If the version of the WebViewer is changed (e.g. from 10.7.2 to 10.7.3), please update the angular.json file accordingly (search for "src/lib/webviewer").
		WebViewer(
			{				
				licenseKey:	environment.webViewer.licenceKey,
				path: `../../../lib/webviewer/${environment.webViewer.version}`,
				isReadOnly: true, 
				preloadWorker: "PDF,OFFICE,LEGACY_OFFICE", 
				disabledElements: disabledElements   
			},
			this.viewer.nativeElement
		).then((instance) => {
			this.wvInstance = instance;						
			instance.UI.disableFeatures([
				instance.UI.Feature.Annotations,
				instance.UI.Feature.Download,
				instance.UI.Feature.ComparePages
			]);

			instance.UI.setToolbarGroup(instance.UI.ToolbarGroup.VIEW);
			if (!this.isMobileDevice) {
				instance.UI.openElements(['leftPanel', 'thumbnailsPanel']);
			}

			if (this.isFitToPage) {
				instance.UI.setFitMode(instance.UI.FitMode.FitPage);
			} else {
				instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
			}
			instance.UI.setZoomLevel('100%');
			instance.UI.useEmbeddedPrint(false); // disable embedded printing			
			instance.Core.documentViewer.addEventListener("documentLoaded", this.wvDocumentLoadedHandler);

			this.viewer.nativeElement.addEventListener("pageChanged", (e) => {
				const [pageNumber] = e.detail;
			});

			this.wvInstance.UI.iframeWindow.addEventListener("loaderror", (err) => {
				this.invalidDocument();
			});
			// if (this.setHeaderElements) {
			// 	this.wvInstance.setHeaderItems(header => {
			// 		this.setHeaderElements(header);
			// 	});
			// }

			if (this._documentDetails) {
				this.loadDocument(
					this._documentDetails.content,
					this._documentDetails.documentDisplayName,
					this.docName(this._documentDetails)
				);
			}

			if (!!this.afterWebViewerRender) {
				this.afterWebViewerRender(this.wvInstance.UI);
			}
			this.isViewerReady = true;
		});
	}

	private invalidDocument(): void {
		this.isLoadingFailed = true;
		this.loaderMessage = "Preview not available for this document type.";
		this.changeDetector.detectChanges();
		if (!!this.documentDetails.documentId) {
			this.documentNotSupported.emit(this.documentDetails);
		}
	}
}
